import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import redditPixel from '../lib/tracking';
import { Target, Shield, Zap, ArrowRight, Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { CodyFullLogo, CodyIconBadge } from '../components/CodyLogo';
import { toast } from 'sonner';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const data = await login(email, password);
      if (data.user?.role === 'admin') {
        navigate('/ops');
      } else {
        toast.success('Welcome back!');
        redditPixel.login(email);
        navigate('/dashboard');
      }
    } catch (err) { toast.error(err.message || 'Login failed'); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left — Branding (desktop only) */}
      <div className="hidden lg:flex lg:w-1/2 bg-[#0B0F17] relative overflow-hidden flex-col justify-center px-16 xl:px-24">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute w-[400px] h-[400px] rounded-full" style={{ background: 'radial-gradient(circle, rgba(255,69,0,0.1) 0%, transparent 70%)', top: '-10%', left: '-10%' }} />
          <div className="absolute w-[300px] h-[300px] rounded-full" style={{ background: 'radial-gradient(circle, rgba(255,176,32,0.06) 0%, transparent 70%)', bottom: '10%', right: '5%' }} />
        </div>

        <div className="relative">
          <div className="mb-10">
            <CodyFullLogo height={32} className="brightness-0 invert" />
          </div>

          <h1 className="text-[2.5rem] xl:text-[3rem] font-extrabold text-white leading-[1.1] mb-5" style={{ fontFamily: "'Space Grotesk'" }}>
            Welcome back.
          </h1>
          <p className="text-lg text-[#8B95A9] mb-10 leading-relaxed max-w-lg">
            Your leads are waiting. Log in to see what Reddit buyers are saying about products like yours.
          </p>

          <div className="space-y-4">
            {[
              'New leads refreshed daily',
              'AI replies ready to generate',
              'Intent-scored opportunities waiting',
            ].map((item, i) => (
              <div key={i} className="flex items-center gap-3">
                <div className="w-5 h-5 rounded-full bg-[#16A34A]/20 flex items-center justify-center flex-shrink-0">
                  <Check className="w-3 h-3 text-[#16A34A]" />
                </div>
                <span className="text-sm text-[#C1C7D0]">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right — Login Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-6 sm:p-12 bg-white">
        <div className="w-full max-w-md">
          <div className="flex items-center justify-center gap-2 mb-8 lg:hidden">
            <CodyIconBadge size={44} />
            <span className="text-xl font-semibold" style={{ fontFamily: "'Space Grotesk'" }}>Cody</span>
          </div>

          <div className="hidden lg:block mb-8">
            <h2 className="text-2xl font-bold mb-1" style={{ fontFamily: "'Space Grotesk'" }}>Log in to your account</h2>
            <p className="text-sm text-[#5B667A]">Pick up where you left off</p>
          </div>

          <div className="lg:hidden text-center mb-6">
            <h2 className="text-xl font-bold mb-1" style={{ fontFamily: "'Space Grotesk'" }}>Welcome back</h2>
            <p className="text-sm text-[#5B667A]">Log in to your account</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="text-sm font-medium text-[#0B0F17] mb-1.5 block">Email</label>
              <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" required className="h-12 rounded-xl" data-testid="login-email-input" />
            </div>
            <div>
              <label className="text-sm font-medium text-[#0B0F17] mb-1.5 block">Password</label>
              <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Enter your password" required className="h-12 rounded-xl" data-testid="login-password-input" />
            </div>
            <Button type="submit" disabled={loading} className="w-full h-12 rounded-xl font-bold text-[15px] cta-btn shadow-lg shadow-[#FF4500]/20" style={{ background: 'linear-gradient(135deg, #FF6A2A 0%, #FF4500 100%)' }} data-testid="login-submit-button">
              {loading ? 'Logging in...' : 'Log In'} {!loading && <ArrowRight className="ml-2 w-4 h-4" />}
            </Button>
          </form>

          <div className="text-center mt-4">
            <Link to="/forgot-password" className="text-xs text-[#5B667A] hover:text-[#FF4500]">Forgot your password?</Link>
          </div>

          <p className="text-sm text-[#5B667A] text-center mt-5">
            Don't have an account? <Link to="/signup" className="text-[#FF4500] font-medium hover:underline" data-testid="signup-link">Sign up free</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
